import { q1DocFunctions } from './q1-core-functions.js';
import { capFirstChar, estimateReadingTime, getFirstParagraph } from './string-helpers-client.js';
import { getMostRecentMonday } from './date-client-helpers.js';
import {
  logItemStatusMap,
  logItemVisibilityMap,
  nonPublishedVisibility,
} from '../models/log-item-client-enums.js';
import {
  updateLimit_daily,
  updateLimit_weekly,
} from '../models/log-item-client-settings.js';

const FIRST_PARAGRAPH_MIN_LENGTH = 200;


/**
 * Enriches the log item with additional properties for display purposes.
 * 
 * @param {Object} logItem - Required. The log item object to enrich.
 * @param {Object} authorMembersByRef - Optional. If provided, a `authors_members` property will be 
 *   added to the log item. The keys should be the author refs, the values the member data.
 *   Note: The member data should already be enriched if that's desired.
 */
export const enrichLogItem = (logItem, authorMembersByRef = null) => {
  if (logItem.isEnriched) return logItem;

  logItem.isEnriched = true;
  
  // First create some shortcuts
  const { subjectCollection, visibility, status } = logItem;

  const statusSettings = logItemStatusMap[status] || {};
  const visibilitySettings = status === 'published'
    ? logItemVisibilityMap[subjectCollection]?.[visibility] || {}
    : nonPublishedVisibility;

  const subjectCollection_titleCase = capFirstChar(subjectCollection);
  const subjectCollection_titleCaseSingular = subjectCollection_titleCase.slice(0, -1);
  const subjectCollection_singular = subjectCollection.slice(0, -1);

  const [status_timestampLabel, status_timestamp] = {
    draft: ['Draft Created', logItem.createdAt],
    published: ['Published', logItem.publishedAt],
    archived: ['Archived', logItem.archivedAt],
    deleted: ['Deleted', logItem.deletedAt],
  }[status];

  let authors_members = authorMembersByRef
    ? logItem.authors?.map(ref => authorMembersByRef[ref] || null).filter(m => m)
    : null;

  const enrichedLogItem = {
    ...logItem,

    subjectCollection_titleCase,
    subjectCollection_titleCaseSingular,
    subjectCollection_singular,

    body_firstParagraph: getFirstParagraph(logItem.body, FIRST_PARAGRAPH_MIN_LENGTH),

    readingTime: estimateReadingTime(logItem.body),
    readingTime_text: `${estimateReadingTime(logItem.body)} min read`,

    editCount_text: logItem.editCount === 0
      ? 'No edits'
      : (logItem.editCount === 1 ? '1 edit' : `${logItem.editCount} edits`),

    status_timestampLabel,
    status_timestamp,

    status_label: statusSettings.label || 'Unknown',
    status_icon: statusSettings.icon || 'help',
    status_iconLibrary: statusSettings.iconLibrary || 'material',
    status_description: statusSettings.description || '',

    visibility_label: visibilitySettings.label || 'Unknown',
    visibility_icon: visibilitySettings.icon || 'help',
    visibility_iconLibrary: visibilitySettings.iconLibrary || 'material',
    visibility_description: visibilitySettings.description || '',

    authors_members,
  };

  return enrichedLogItem;
}

/**
 * Use this to update just the status in a view with no form.
 * 
 * DO NOT USE this method if there is a form, then updates need to be handled locally in the view 
 * in order to properly communicate form validation errors.
 * 
 * This method will show toast messages for success or error. You can use `then` and `catch` 
 * on the returned promise to handle any additional logic needed in the view.
 * 
 * @param {string} logItemId - Required. The id of the log item to update.
 * @param {string} newLogItemStatus - Required. The new status to set for the log item.
 * @param {function} showToastFunction - Required. The function to call to show a toast message.
 * @param {boolean} [throwError=false] - Optional. If true, will throw an error if the update fails.
 *   If false, the error is handled and a toast message is shown instead.
 * 
 * @returns {Promise<void>} - A promise that resolves when the log item is updated.
 */
export const updateLogItemStatus = async (
  logItemId, newLogItemStatus, showToastFunction, throwError = false
) => {
  const { updateDoc } = q1DocFunctions;
  
  return updateDoc({
    docCollection: 'logItems',
    docId: logItemId,
    docUpdate: { status: newLogItemStatus },
  }).then((response) => {
    showToastFunction(null, 'Log item status updated successfully!', 'success');
  }).catch((error) => {
    console.error('Error calling function:', error);
    if (throwError) throw error;
    
    showToastFunction(error.message, 'There was an error updating the log item status!', 'danger');
  });
}

/**
 * Generates an object containing the member-facing rate limit details for log item posting based
 * on the subject's activation status and the log item stats.
 * 
 * @param {string} subjectActivationStatus - Required. The activation status of the subject.
 * @param {Object} logItemStats - Required. The stats object returned by log item history function.
 *   Must contain the following keys: `logItemCounts.today`, `logItemCounts.thisWeek`
 * 
 * @return {Object} - An object containing the rate limit details for log item posting.
 * The object will have the following keys:
 * - `canPostToday`: A boolean indicating if the member can post today. Note: It's possible that 
 *   the member can post today but not this week. (They hit the daily limit but not the weekly.)
 * - `canPostThisWeek`: A boolean indicating if the member can post this week.
 * - `currentUsageMessage`: A string indicating explaining the current usage. (You've used X of Y)
 * - `postingTodayError`: A string that contains an error message if the member has hit the daily
 *   OR the weekly limit. Empty string if the member can post today and this week.
 * - `postingThisWeekError`: A string that contains an error message if the member has hit the
 *   weekly limit. Empty string if the member can post this week.
 * - `dailyLimit`: The daily limit for log item posting.
 * - `weeklyLimit`: The weekly limit for log item posting.
 * - `countToday`: The number of log items posted today.
 * - `countThisWeek`: The number of log items posted this week.
 */
export const logItemRateLimitDetails = (subjectActivationStatus, logItemStats) => {
  const { today: countToday, thisWeek: countThisWeek } = logItemStats.logItemCounts;
  const dailyLimit = updateLimit_daily[subjectActivationStatus] || 1;
  const weeklyLimit = updateLimit_weekly[subjectActivationStatus] || 1;

  const currentUsageMessage =
    `You've used ${countToday} of ${dailyLimit} available updates for today `
    + `and ${countThisWeek} of ${weeklyLimit} available updates for the week.`;
  
  const canPostToday = countToday < dailyLimit;
  const canPostThisWeek = countThisWeek < weeklyLimit;

  const postingTodayError = canPostThisWeek
    ? canPostToday ? '' : `You've hit the daily limit! It resets tomorrow.`
    : `You've hit the weekly limit! It resets on Monday.`;
  const postingThisWeekError = canPostThisWeek 
    ? '' 
    : `You've hit the weekly limit! It resets on Monday.`;

  return {
    canPostToday,
    canPostThisWeek,
    currentUsageMessage,
    postingTodayError,
    postingThisWeekError,

    dailyLimit,
    weeklyLimit,
    countToday,
    countThisWeek,
  };
};

/**
 * Returns an object outlining whether a log item can be posted with the given publishDate.
 * 
 * @param {string} subjectActivationStatus - Required. The activation status of the subject.
 * @param {Object} logItemStats - Required. The stats object returned by log item history function.
 * @param {Date|string} publishDate - Required. The publishedAt value of the log item. Should be
 *   a Date object or an ISO string.
 * 
 * @return {Object} - An object containing the following keys:
 * - `isAllowed`: A boolean indicating if the log item can be posted.
 * - `errorMessage`: If `isAllowed` is false, this will contain an error message explaining why.
 */
export const getLogItemPublishDateAllowed = (subjectActivationStatus, logItemStats, publishDate) =>{
  if (typeof publishDate === 'string') publishDate = new Date(publishDate);

  const now = new Date();
  const publishDateString = publishDate.toLocaleDateString('en-CA');
  const todayDateString = new Date().toLocaleDateString('en-CA');
  const mostRecentMonday = getMostRecentMonday();

  // Scenario 1: Future date
  if (publishDate > now) return {
    isAllowed: false,
    errorMessage: 'The publish date cannot be in the future.',
  };

  const {
    canPostToday, canPostThisWeek, postingTodayError, postingThisWeekError,
  } = logItemRateLimitDetails(subjectActivationStatus, logItemStats);

  // Scenario 2: Today's date
  if (publishDateString === todayDateString) return {
    isAllowed: canPostToday && canPostThisWeek,
    errorMessage: postingTodayError,
  };

  // Scenario 3: Past date, this week
  if (publishDate >= mostRecentMonday) return {
    isAllowed: canPostThisWeek,
    errorMessage: postingThisWeekError,
  };
  
  // Scenario 4: Past date, prior week
  return {
    isAllowed: true,
    errorMessage: '',
  };
};
